<script setup lang="ts">
import { definePage } from "unplugin-vue-router/runtime";
import { useForm } from "vee-validate";
import { useI18n } from "vue-i18n";
import { object, ref as yupRef } from "yup";
import { ref } from "vue";
import { useRoute, useRouter } from "vue-router";
import UsersService from "@/api/models/auth/users";
import { useSnackStore } from "@/stores/snackStore";
import { createRequiredField } from "@/utils/validationHelpers";
import PasswordSafetyIndicator from "@/components/shared/PasswordSafetyIndicator.vue";
import { useFormSubmitFlowLock } from "@/composables/shared/useFormSubmitFlowLock";
import { useGenericMutation } from "@/composables/shared/mutations/useGenericMutation";

definePage({
  meta: {
    title: "models.auth.invitation.title",
    layout: "login",
    requiresAuth: false,
  },
});

const { t } = useI18n();
const snackStore = useSnackStore();
const route = useRoute();
const router = useRouter();

const id = (route.params as { id: string }).id;
const token = route.query.token as string;
const redirectPath = route.query.redirect as string | undefined;

const password = ref("");
const isPasswordValid = ref(false);

const validationSchema = object({
  password: createRequiredField(t, "generic.fields.password"),
  confirmPassword: createRequiredField(t, "confirmPassword").oneOf(
    [yupRef("password")],
    t("models.auth.resetPasswordConfirm.passwordsMatch")
  ),
});

const { handleSubmit } = useForm({
  validationSchema,
});

const { mutateAsync: performAcceptInvitation, isPending } = useGenericMutation<void, string>({
  mutationFn: async (password) => {
    // TODO: Replace with actual invitation acceptance API call
    return await UsersService.resetPasswordConfirm(password, id, token);
  },
  options: {
    onSuccess: async (): Promise<void> => {
      snackStore.showSuccess(t("models.auth.setPassword.success"));
      await router.push({ path: "/auth/login", query: { redirect: redirectPath } });
    },
  },
});

const { runSubmitFlow, isSubmitFlowPending } = useFormSubmitFlowLock();

const submit = handleSubmit((values) => {
  if (!isPasswordValid.value) {
    return;
  }
  void runSubmitFlow(async () => {
    await performAcceptInvitation(values.password);
  });
});

const showPassword = ref(false);
const showConfirmPassword = ref(false);
</script>

<template>
  <div
    class="tw:w-full tw:md:w-1/2 tw:md:!ml-auto tw:h-full tw:flex tw:items-center tw:justify-center tw:!px-6 tw:md:!px-0"
  >
    <v-form
      class="tw:w-full tw:md:w-2/3"
      :validation-schema="validationSchema"
      @submit.prevent="submit"
    >
      <div class="tw:font-bold tw:text-2xl tw:md:text-4xl tw:!mb-1">
        {{ t("models.auth.invitation.title") }}
      </div>
      <div class="tw:!mt-10">
        <text-field
          v-model="password"
          name="password"
          :label="t('generic.fields.password')"
          :type="showPassword ? 'text' : 'password'"
          :append-inner-icon="showPassword ? 'mdi-eye-off' : 'mdi-eye'"
          @click:append-inner="showPassword = !showPassword"
        />
      </div>
      <div>
        <text-field
          name="confirmPassword"
          :label="t('models.auth.invitation.confirmPassword')"
          :type="showConfirmPassword ? 'text' : 'password'"
          :append-inner-icon="showConfirmPassword ? 'mdi-eye-off' : 'mdi-eye'"
          @click:append-inner="showConfirmPassword = !showConfirmPassword"
        />

        <password-safety-indicator
          v-model:is-valid="isPasswordValid"
          :password="password"
          class="tw:!mt-3"
        />
      </div>
      <div class="tw:flex tw:justify-between tw:items-center tw:!mt-8">
        <v-btn
          class="tw:!text-xs"
          rounded
          size="large"
          min-width="150"
          variant="flat"
          color="teal"
          type="submit"
          :loading="isSubmitFlowPending || isPending"
        >
          {{ t("models.invitations.acceptInvitation") }}
        </v-btn>
      </div>
    </v-form>
  </div>
</template>
